import React, {useState, useEffect} from 'react';
import SystemUserWizardBody from './SystemUserWizardBody';
import {SystemUserCreateSchema} from '../../../api/DBSchema/SystemUser';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes} from '../../../api/consts';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import SMCModal from '../../../components/core/SMCModal';
import {useTranslation} from 'react-i18next';
import {useIsMountedRef} from '../../../utils/hooks';

function SystemUserWizard({
    isOpen,
    onRequestClose,
    onSuccess
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const isMountedRef = useIsMountedRef();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [availableSystemRoles, setAvailableSystemRoles] = useState([]);
    const [isTwoStepAuthenticationEnabled, setIsTwoStepAuthenticationEnabled] = useState(false);

    const [uid, setUid] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');
    const [password, setPassword] = useState('');

    const onFinish = async () => {
        const createSystemUserRequestObj = {
            [SystemUserCreateSchema.name]: uid,
            [SystemUserCreateSchema.firstName]: firstName,
            [SystemUserCreateSchema.lastName]: lastName,
            [SystemUserCreateSchema.email]: email,
            [SystemUserCreateSchema.role]: roleId,
            [SystemUserCreateSchema.password]: password,
        }
        setIsLoading(true);
        Api.createObject(ObjectTypes.SYSTEM_USER, createSystemUserRequestObj)
        .then(() => {
            if (isMountedRef.current) {
                onSuccess(uid);
            }
        })
        .catch (e => {
            if (isMountedRef.current) {
                const errorMessage = getErrorMessage(e);
                alert(errorMessage);
            }
        })
        .finally(() => {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        });
    }

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const sessData = await Api.getSessionData({
                    attributes: [
                        SessionDataGetSchema.availableSystemRoles,
                        SessionDataGetSchema.isSystemTwoFactorAuthenticationEnabled
                    ]
                });
                const roles = sessData.data.availableSystemRoles;
                const isSystemTwoFactorAuthenticationEnabled = sessData.data.isSystemTwoFactorAuthenticationEnabled;
                if (Array.isArray(roles)) {
                    setAvailableSystemRoles(roles);
                    // Set role id to be first in list
                    setRoleId(roles[0]);
                    setIsTwoStepAuthenticationEnabled(!!isSystemTwoFactorAuthenticationEnabled);
                } else {
                    throw new Error(ApiErrorCodes.GENERAL_ERROR);
                }
            } catch (e) {
                const errorMessage = getErrorMessage(e);
                setErrorMessage(errorMessage);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [getErrorMessage]);

    // Reset to defaults on every open
    useEffect(() => {
        if (isOpen) {
            setUid('');
            setFirstName('');
            setLastName('');
            setEmail('');
            Array.isArray(availableSystemRoles) && setRoleId(availableSystemRoles[0]);
            setPassword('');
        }
    }, [isOpen, availableSystemRoles]);

    const wizardVars = {
        onRequestClose,
        onFinish,
        uid, setUid,
        firstName, setFirstName,
        lastName, setLastName,
        email, setEmail,
        roleId, setRoleId,
        password, setPassword,
        isTwoStepAuthenticationEnabled,
        availableSystemRoles
    }

    return (
        <SMCModal
            title={t('SMP.SYSTEMUSERWIZ.CREATE_NEW_USER')}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={610}
            height={600}
            loading={isLoading}
            errorMessage={errorMessage}
        >
            <SystemUserWizardBody wizardVars={wizardVars} />
        </SMCModal>
    );
}

export default SystemUserWizard;